<template>
  <v-dialog v-model="dialog" persistent fullscreen>
    <v-toolbar color="primary">
      <v-spacer />
      <v-btn text dark @click="onClose">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-card>
      <v-row>
        <v-col cols="12">
          <v-container>
            <div class="flex">
              <v-combobox v-model="roleInputs.name" :error-messages="roleInputs.errors.name" :items="roles.items" hide-details="auto"
                          dense outlined label="Role Name" @change="onRoleNameChange" />
            </div>
          </v-container>
        </v-col>
        <v-col cols="12">
          <v-container>
            <v-simple-table>
              <template #default>
                <thead>
                  <tr>
                    <th>Permission</th>
                    <th>Allow</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="permission in permissions" :key="permission">
                    <th>{{ permission.id.toUpperCase().replace('_', ' ').replace(':', ' - ') }}</th>
                    <td>
                      <div style="height: 44px;display: flex;align-items: center;">
                        <v-switch v-model="permission.allow" style="margin-top:0px;" hide-details="auto" @change="(val) => {
                          roleInputs.items[permission.id] = { permission_id: permission.id, allow: val }
                          rolesAdded++
                        }" />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colspan="2" style="-webkit-app-region: no-drag">
                      <div style="">
                        <v-btn :disabled="!rolesAdded" color="primary" @click="save">
                          Save
                        </v-btn>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-container>
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapState } from 'vuex'
import { useWatchEffect } from '@/lib/utils'
import appAxios from '@/extra-functions/axios.extra'
import { isAxiosError } from 'axios'
import { ErrorPopup, LoadingPopup, SuccessPopup } from '../../../../../../lib/utils'

const [watchEffect, clearWatchEffect] = useWatchEffect()

const roleInputsDefault = () => ({
  items: {},
  name: '',
  errors: {},
})

export default {
  name: 'CreateRole',
  data() {
    return {
      dialog: false,
      permissions: [],
      roleInputs: roleInputsDefault(),
      rolesAdded: 0,
      roles: {
        items: [],
        loading: false,
      },
    }
  },
  created() {
    this.getRoles()
  },
  destroyed() {
    clearWatchEffect()
  },
  computed: {
    ...mapState({
      authToken: state => state.auth.token,
    }),
  },
  methods: {
    open(retries = 0) {
      watchEffect(async (onCleanup) => {
        try {
          const axios = appAxios(this.authToken)
          LoadingPopup.fire({ text: 'Getting List of Permissions' })
          const controller = new AbortController()
          onCleanup(() => {
            controller.abort()
          })
          const { data: permissions } = await axios.get('/admin/permissions')
          this.permissions = permissions
          this.rolesAdded = 0
          this.dialog = true
          LoadingPopup.close()
        } catch (e) {
          console.error(e)
          if (retries > 5) {
            if (isAxiosError(e) && e.response.status === 500) {
              ErrorPopup.fire({ text: 'Server Error!' })
              return
            }
            ErrorPopup.fire()
            return
          }
          setTimeout(() => this.open(retries + 1), 5000)
        }
      }, 'MANAGE_ROLE_OPEN')
    },
    getRoles(retries = 0) {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          this.roles = {
            ...this.roles,
            loading: true,
          }
          onCleanup(() => {
            controller.abort()
          })
          const axios = appAxios(this.authToken)
          const { data: roles } = await axios.get('/admin/roles')
          this.roles = {
            items: roles,
            loading: false,
          }
        } catch (e) {
          this.roles = {
            ...this.roles,
            loading: false,
          }
          console.error(e)
          if (retries > 5) {
            if (isAxiosError(e) && e.response.status === 500) {
              ErrorPopup.fire({ text: 'Server Error!' })
              return
            }
            ErrorPopup.fire()
            return
          }
          setTimeout(() => this.roles(retries + 1), 5000)
        }
      }, 'MANAGE_ROLE_GET_ROLES')
    },
    async save() {
      try {
        this.roleInputs.errors = []
        LoadingPopup.fire()
        const { items, name } = this.roleInputs
        const permissions = Object.keys(items).reduce((prev, next) => {
          prev.push(items[next])
          return prev
        }, [])
        const axios = appAxios(this.authToken)
        await axios.post('/admin/roles', {
          name,
          permissions,
        })
        SuccessPopup.fire({
          text: 'successfully added new role',
        })
      } catch (e) {
        console.error(e)
        if (isAxiosError(e)) {
          if (e.response.status === 500) {
            ErrorPopup.fire({
              text: 'Server error!',
            })
            return
          }
          ErrorPopup.fire({
            text: 'entered an incorrect input (check role name? OR did you allow any permissions?)',
          })
          this.roleInputs = {
            ...this.roleInputs,
            errors: e.response.data?.errors,
          }
          return
        }
        ErrorPopup.fire()
      }
    },
    onRoleNameChange(value) {
      const roleSet = new Set([...this.roles.items])
      if (!roleSet.has(value)) return
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => {
            controller.abort()
          })
          LoadingPopup.fire({ text: 'Please Wait While Getting Role Permissions From Server!' })
          const axios = appAxios(this.authToken)
          const { data: permissions } = await axios.get(`/admin/roles/${value}/permissions`, {
            signal: controller.signal,
          })
          this.permissions = this.permissions.map(item => {
            if (permissions[item.id]) {
              item.allow = permissions[item.id].allow
            }
            return item
          })
          this.rolesAdded = 0
          LoadingPopup.close()
        } catch (e) {
          console.error(e)
          if (isAxiosError(e) && e.response.status === 500) {
            ErrorPopup.fire({ text: 'Server Error!' })
            return
          }
          ErrorPopup.fire()
        }
      }, 'MANAGE_ROLES_ON_ROLE_NAME_CHANGE')
    },
    onClose() {
      this.roleInputs = roleInputsDefault()
      this.dialog = false
    },
  },
}
</script>
